import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { Iata, PlatformThemeId } from '@reward-platform/platform-config'
import { isClient, isLocal, isProduction, isStaging } from '~/utils/envChecks'
import {
  getBrowserFromUserAgent,
  getDomain,
  getPageTitle,
  getPlatformFromUserAgent,
} from '~/utils/windowNavigator'
import { NEXT_API_URL } from '~/utils/apiConfig'
import { usePlatformConfig } from '~/context/platform'
import { useAuthContext } from '~/context/auth'
import { usePlatformTheme } from '~/context/theme'
import { addQueryToUrlPath } from '~/utils/url'
import { isChatbotEnabledForOpCo } from '~/utils/chatbot'

const CHATBOT_CONTAINER_ID = 'chatbot'
const userAgent = isClient() ? window.navigator.userAgent : 'SSR'

const isChatWidgetEnabled = () => isClient() && !!window.AmazonCustomChatWidget

const SERVICE_PROVIDERS = {
  BA: 'RP_BA',
  EI: 'RP_EI',
  AVIOS_BA: 'AVIOS_RP_BA',
}

const isLowerEnv = !isStaging() && !isProduction()

const getServiceProvider = (opCoIata: Iata, theme?: PlatformThemeId) => {
  const isWhitelabelPlatform = theme !== 'avios'
  if (isWhitelabelPlatform) {
    switch (opCoIata) {
      case 'BA':
        return SERVICE_PROVIDERS.BA
      case 'EI':
        return SERVICE_PROVIDERS.EI
      default:
        return SERVICE_PROVIDERS.AVIOS_BA
    }
  }

  switch (opCoIata) {
    // case 'IB':
    case 'BA':
    case 'EI':
    default:
      return SERVICE_PROVIDERS.AVIOS_BA
  }
}

const useChatWidget = () => {
  const { opCo } = usePlatformConfig()
  const router = useRouter()
  const { themeId } = usePlatformTheme()
  const url = process.env.NEXT_PUBLIC_CHAT_WIDGET_URL
  const { isAuthenticated } = useAuthContext()

  useEffect(() => {
    const widget = window.AmazonCustomChatWidget?.ChatInterface
    let chatWidget: any
    const startChatPath = `${NEXT_API_URL}/chatbot/startChat`
    const apiUrl = `${getDomain()}${startChatPath}`

    if (!isChatbotEnabledForOpCo(opCo)) {
      window.sessionStorage.removeItem('openChat')
    } else if (url && widget && isChatWidgetEnabled() && isAuthenticated) {
      try {
        chatWidget = widget.init({
          containerId: CHATBOT_CONTAINER_ID,
          serviceProvider: getServiceProvider(opCo, themeId),
          additionalData: {
            channel: 'Webchat',
            platform: getPlatformFromUserAgent(userAgent),
            browser: getBrowserFromUserAgent(userAgent),
          },
          startTime: new Date().toISOString(),
          pageTitle: getPageTitle(router.asPath),
          language: 'en',
          opCo,
          apiUrl,
        })

        const openChat = window.sessionStorage.getItem('openChat') === 'true'
        if (openChat) {
          setTimeout(() => {
            const chatBot = document.getElementById('chatbot')?.childNodes[0] as HTMLButtonElement
            chatBot?.click()
          }, 0)
          window.sessionStorage.removeItem('openChat')
        }
      } catch (error) {
        if (!isLocal()) {
          // eslint-disable-next-line no-console
          console.warn('Chat widget is missing configuration!', error)
        }
      }
    }
    return () => {
      if (chatWidget?.detach) {
        chatWidget.detach()
      }
    }
  }, [router.asPath, router, url, isAuthenticated, themeId, opCo])
}

const CHATBOT_QUERY_KEY = 'chatbot'
const CHATBOT_OPEN_VALUE = 'open'

export const useChatbotOpen = () => {
  const router = useRouter()
  const { opCo } = usePlatformConfig()
  const currentLink = addQueryToUrlPath(router.asPath, {
    ...router.query,
    [CHATBOT_QUERY_KEY]: CHATBOT_OPEN_VALUE,
  })
  if (!isChatbotEnabledForOpCo(opCo)) {
    return () => undefined
  }
  return () => router.push(currentLink)
}

export default useChatWidget
